// StudioToolbar — the Studio's vertical tool bar (pen B4, "Verktygsrad
// (vertikal)"): floating at the canvas's left edge, vertically centred, a
// fixed size whatever the state (icons never move). Pure: the active tool,
// whether the app menu shows and whether the server is reachable come in;
// every choice goes out as an intent.
//
// Top to bottom: Menu · Select (V), Move the canvas (H) · Container ▾ (C) ·
// Source (S), Material ▾ (M). The tooltip names the tool and its key. What
// needs the server is greyed out offline, with the reason on hover (it stays
// focusable so the reason can be read).

export { StudioToolbar };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import type { NewStudioMaterial } from "../../app/AppShellHost/useStudioCommands.js";
import type { ContainerKind } from "../../app/containerFacts.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Kbd } from "../Kbd/Kbd.js";
import { Menu } from "../Menu/Menu.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import type { Tool } from "./studioTools.js";
import "./StudioToolbar.css";

type StudioToolbarProps = {
    readonly tool: Tool;
    readonly menuShown: boolean;
    readonly online: boolean;
    // The Material menu, open from the bar or from the M key.
    readonly materialMenuOpen: boolean;
    readonly onMaterialMenuOpenChange: (open: boolean) => void;
    readonly onToggleMenu: () => void;
    readonly onPickTool: (tool: Tool) => void;
    // The Container menu (pen B4): the kind last chosen is checked; choosing
    // one remembers it, then wraps the selection if there is one, else picks
    // the tool.
    readonly containerKind: ContainerKind;
    readonly onContainer: (kind: ContainerKind) => void;
    readonly onUpload: () => void;
    readonly onNewMaterial: (kind: NewStudioMaterial) => void;
};

type ToolButtonProps = Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "aria-label" | "children"
> & {
    readonly label: string;
    readonly icon: IconName;
    readonly keyHint?: string;
    readonly pressed?: boolean;
    readonly offline?: boolean;
    readonly hasMenu?: boolean;
    // The assistant's tool: its mark in the agent colour.
    readonly agent?: boolean;
};

const StudioToolbar = ({
    tool,
    menuShown,
    online,
    materialMenuOpen,
    onMaterialMenuOpenChange,
    onToggleMenu,
    onPickTool,
    containerKind,
    onContainer,
    onUpload,
    onNewMaterial,
}: StudioToolbarProps): ReactNode => {
    const { t } = useLingui();
    const offline = !online;
    // The tool is named for the kind it makes; "container" is internal.
    const containerLabel = ((): string => {
        switch (containerKind) {
            case "module":
                return t`Module`;
            case "course":
                return t`Course`;
            default:
                return exhaustSwitchCase(containerKind);
        }
    })();
    const container = (
        <ToolButton
            label={containerLabel}
            icon={containerKind}
            keyHint="C"
            pressed={tool === "container"}
            offline={offline}
            hasMenu
        />
    );
    const material = (
        <ToolButton
            label={t`Material`}
            icon="file-plus"
            keyHint="M"
            offline={offline}
            hasMenu
        />
    );
    return (
        <div
            className="bilda-studio-toolbar"
            role="toolbar"
            aria-orientation="vertical"
            aria-label={t`Tools`}
        >
            <ToolButton
                label={t`Menu`}
                icon="panel-left"
                pressed={menuShown}
                onClick={onToggleMenu}
            />
            <span className="bilda-studio-toolbar__rule" aria-hidden="true" />
            <ToolButton
                label={t`Select`}
                icon="pointer"
                keyHint="V"
                pressed={tool === "select"}
                onClick={() => onPickTool("select")}
            />
            <ToolButton
                label={t`Move the canvas`}
                icon="hand"
                keyHint="H"
                pressed={tool === "hand"}
                onClick={() => onPickTool("hand")}
            />
            <span className="bilda-studio-toolbar__rule" aria-hidden="true" />
            {offline ? (
                container
            ) : (
                <Menu
                    trigger={container}
                    side="right"
                    align="start"
                    items={[
                        {
                            _tag: "item",
                            label: t`Module`,
                            icon: <Icon name="module" />,
                            checked: containerKind === "module",
                            onSelect: () => onContainer("module"),
                        },
                        {
                            _tag: "item",
                            label: t`Course`,
                            icon: <Icon name="course" />,
                            checked: containerKind === "course",
                            onSelect: () => onContainer("course"),
                        },
                    ]}
                />
            )}
            <ToolButton
                label={t`Source`}
                icon="file-up"
                keyHint="S"
                offline={offline}
                onClick={onUpload}
            />
            {offline ? (
                material
            ) : (
                <Menu
                    trigger={material}
                    side="right"
                    align="start"
                    open={materialMenuOpen}
                    onOpenChange={onMaterialMenuOpenChange}
                    items={[
                        {
                            _tag: "item",
                            label: t`New quiz`,
                            icon: <Icon name="mcq" />,
                            onSelect: () => onNewMaterial("quiz"),
                        },
                        {
                            _tag: "item",
                            label: t`New document`,
                            icon: <Icon name="document" />,
                            onSelect: () => onNewMaterial("document"),
                        },
                    ]}
                />
            )}
            <span className="bilda-studio-toolbar__rule" aria-hidden="true" />
            <ToolButton
                label={t`Assistant here`}
                icon="sparkle"
                keyHint="A"
                agent
                offline={offline}
                pressed={tool === "assistant"}
                onClick={() => onPickTool("assistant")}
            />
        </div>
    );
};

// One 36 px tool. Offline it stays focusable (aria-disabled, not disabled) so
// its tooltip can say why; a click then does nothing. Props beyond these are
// a Menu trigger's (its ref and handlers), forwarded to the button.
const ToolButton = forwardRef<HTMLButtonElement, ToolButtonProps>(
    (
        {
            label,
            icon,
            keyHint,
            pressed,
            offline = false,
            hasMenu = false,
            agent = false,
            onClick,
            ...trigger
        },
        ref,
    ) => {
        const { t } = useLingui();
        return (
            <Tooltip
                ref={ref}
                side="right"
                content={
                    offline ? (
                        t`Needs a connection`
                    ) : (
                        <span className="bilda-studio-toolbar__tip">
                            {label}
                            {keyHint === undefined ? null : (
                                <Kbd keys={[keyHint]} />
                            )}
                        </span>
                    )
                }
            >
                <button
                    type="button"
                    className="bilda-studio-toolbar__tool"
                    aria-label={label}
                    aria-pressed={pressed}
                    aria-disabled={offline ? true : undefined}
                    aria-keyshortcuts={keyHint}
                    data-menu={hasMenu ? true : undefined}
                    data-agent={agent ? true : undefined}
                    {...trigger}
                    onClick={offline ? undefined : onClick}
                >
                    <Icon name={icon} />
                    {hasMenu ? (
                        <span
                            className="bilda-studio-toolbar__chevron"
                            aria-hidden="true"
                        >
                            <Icon name="chevron-down" size="xs" />
                        </span>
                    ) : null}
                </button>
            </Tooltip>
        );
    },
);
ToolButton.displayName = "ToolButton";
